// src/data/helpContent.ts
// ----------------------
// Repository for the `help_content` table — the per-key help text shown in the
// read-only "?" bubbles + the shelter welcome guide, edited via Admin → Help
// Content. Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type HelpContentRow = Database["public"]["Tables"]["help_content"]["Row"];
type HelpContentInsert = Database["public"]["Tables"]["help_content"]["Insert"];

// All help_content rows (the admin editor builds a key→row map). Null on error.
export async function listHelpContent(): Promise<HelpContentRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c.from("help_content").select("*");
  if (error) {
    logger.warn("listHelpContent failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as HelpContentRow[];
}

// The text for a single help key ("?" bubbles + the shelter welcome guide).
// maybeSingle so a key with no row resolves to null (not a 406), never logged.
export async function getHelpText(key: string): Promise<string | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c.from("help_content").select("text").eq("key", key).maybeSingle();
  if (error) {
    logger.warn("getHelpText failed:", error.message);
    return null;
  }
  return (data?.text as string | null) ?? null;
}

// Upsert a help_content row (admin editor); conflict resolves on the `key` PK.
export async function upsertHelpContent(
  row: object
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("help_content").upsert(row as HelpContentInsert);
  if (error) logger.warn("upsertHelpContent failed:", error.message);
  return { error };
}
